import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Avatar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CustomButton from './CustomButton';

interface FormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: () => void;
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  loading?: boolean;
  formId?: string;
  cancelText?: string;
  submitText?: string;
  disableSubmit?: boolean;
  hideSubmit?: boolean;
  disableBackdropClick?: boolean;
  headerContent?: React.ReactNode;
  children?: React.ReactNode;
}

export default function FormDialog({
  open,
  onClose,
  onSubmit,
  title,
  subtitle,
  icon,
  maxWidth = 'sm',
  loading = false,
  formId,
  cancelText = 'Cancelar',
  submitText = 'Salvar',
  disableSubmit = false,
  hideSubmit = false,
  disableBackdropClick = false,
  headerContent,
  children,
}: FormDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleClose = (event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (loading) return;
    if (disableBackdropClick && reason === 'backdropClick') return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={maxWidth}
      fullScreen={isMobile}
      PaperProps={{
        sx: { borderRadius: 3 },
      }}
      aria-labelledby="form-dialog-title"
      aria-describedby={subtitle ? "form-dialog-description" : undefined}
    >
      {icon ? (
        <DialogTitle sx={{ px: 4, pt: 4, pb: headerContent ? 0 : 1 }} id="form-dialog-title">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                bgcolor: (t) => `${t.palette.primary.main}14`,
                color: 'primary.main',
              }}
            >
              {icon}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight={700}>
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="body2" color="text.secondary" id="form-dialog-description">
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>
      ) : (
        <DialogTitle sx={{ px: 4, pt: 4, pb: headerContent ? 0 : 1 }} id="form-dialog-title">
          <Typography variant="h6" fontWeight={700}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} id="form-dialog-description">
              {subtitle}
            </Typography>
          )}
        </DialogTitle>
      )}

      {headerContent && (
        <Box sx={{ px: 4 }}>{headerContent}</Box>
      )}

      <DialogContent sx={{ px: 4, py: 3, overflowX: 'hidden' }}>{children}</DialogContent>

      <DialogActions sx={{ px: 4, pb: 4, pt: 1, justifyContent: "center" }}>
        <CustomButton onClick={onClose} disabled={loading} variant="secondary">
          {cancelText}
        </CustomButton>
        {!hideSubmit && (
          <CustomButton
            type={formId ? 'submit' : 'button'}
            form={formId}
            onClick={formId ? undefined : onSubmit}
            variant="primary"
            disabled={loading || disableSubmit}
          >
            {submitText}
          </CustomButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
